import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Base, LockIcon } from '@itsrever/design-system'
import {
  GroupRouting,
  NEW_WAREHOUSE,
  Warehouse,
  matchesSearch,
  storesUsing
} from '@/domain/warehouses'
import {
  useDeleteWarehouse,
  useSaveWarehouse
} from '@/application/manage-warehouses'
import { useCountries } from '@/application/queries/use-countries'
import { useDialog } from '@/ui/components/dialog/dialog-provider'
import { WarehouseForm } from './warehouse-form'
import { WarehousesScope } from '@/hooks/use-warehouses-scope'

const { Badge, Button, Callout, Card, SearchInput, Table, createColumnHelper } =
  Base

const NEW_ID = 'new'
const column = createColumnHelper<Warehouse>()

type Scope = Exclude<WarehousesScope, { kind: 'none' }>

interface WarehouseListProps {
  scope: Scope
  warehouses: Warehouse[]
  routing: GroupRouting
  onShowGroup: () => void
  onShowRules: () => void
}

export const WarehouseList: React.FC<WarehouseListProps> = ({
  scope,
  warehouses,
  routing,
  onShowGroup,
  onShowRules
}) => {
  const { t } = useTranslation()
  const showDialog = useDialog()
  const { countries } = useCountries()
  const editable = scope.kind === 'group'
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState(warehouses[0]?.id ?? '')
  const { saveWarehouse, savingWarehouse } = useSaveWarehouse(scope.group)
  const { deleteWarehouse, deletingWarehouse } = useDeleteWarehouse(scope.group)

  const adding = selectedId === NEW_ID
  const rows = adding ? [...warehouses, NEW_WAREHOUSE] : warehouses
  const visible = rows.filter((warehouse) => matchesSearch(warehouse, search))
  const selected = adding
    ? NEW_WAREHOUSE
    : warehouses.find((warehouse) => warehouse.id === selectedId)

  const country = (warehouse: Warehouse) =>
    countries.find((c) => c.iso === warehouse.countryCode)?.name ||
    warehouse.country ||
    warehouse.countryCode ||
    '—'

  const usage = (warehouse: Warehouse) => {
    const used = storesUsing(routing, scope.stores, warehouse.id)
    return used ? (
      <Badge tone="success">
        {t('warehouses.list.usage_group', { used, total: scope.stores.length })}
      </Badge>
    ) : (
      <span className="text-xs text-neutral-500">
        {t('warehouses.list.unused_group')}
      </span>
    )
  }

  const columns = [
    column.display({
      id: 'company',
      header: t('warehouses.list.column_warehouse'),
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.company || t('warehouses.list.new_warehouse')}
        </span>
      )
    }),
    column.display({
      id: 'city',
      header: t('warehouses.list.column_city'),
      cell: ({ row }) => row.original.city || '—'
    }),
    column.display({
      id: 'country',
      header: t('warehouses.list.column_country'),
      cell: ({ row }) => country(row.original)
    }),
    ...(editable
      ? [
          column.display({
            id: 'usage',
            header: t('warehouses.list.column_usage_group'),
            cell: ({ row }) => (row.original.id ? usage(row.original) : null)
          })
        ]
      : [])
  ]

  const remove = async (warehouse: Warehouse) => {
    const confirmed = await showDialog({
      title: t('warehouses.form.delete_confirm_title', {
        name: warehouse.company
      }),
      message: t('warehouses.form.delete_confirm_message'),
      okButtonText: t('warehouses.form.delete'),
      cancelButtonText: t('warehouses.form.cancel'),
      destructive: true
    })
    if (!confirmed) return
    deleteWarehouse(warehouse.id, {
      onSuccess: () =>
        setSelectedId(warehouses.find((w) => w.id !== warehouse.id)?.id ?? '')
    })
  }

  return (
    <div className="flex flex-col gap-4">
      {editable ? (
        <Callout tone="success" className="text-xs leading-relaxed">
          <Trans
            i18nKey="warehouses.list.group_banner"
            values={{ count: scope.stores.length, group: scope.group }}
            components={{
              b: <b />,
              action: (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  inline
                  onClick={onShowRules}
                />
              )
            }}
          />
        </Callout>
      ) : (
        <Callout
          tone="info"
          icon={<LockIcon />}
          className="text-xs leading-relaxed"
        >
          <Trans
            i18nKey="warehouses.list.store_banner"
            values={{ group: scope.group, store: scope.ecommerceId }}
            components={{
              b: <b />,
              action: (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  inline
                  onClick={onShowGroup}
                />
              )
            }}
          />
        </Callout>
      )}

      <div className="flex items-center gap-3">
        <div className="w-full max-w-[380px]">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('warehouses.list.search')}
            aria-label={t('warehouses.list.search')}
            onClear={() => setSearch('')}
          />
        </div>
        <p className="ml-auto text-xs text-neutral-600">
          {t('warehouses.list.count', { count: warehouses.length })}
        </p>
        {editable ? (
          <Button
            type="button"
            variant="primary"
            disabled={adding}
            onClick={() => setSelectedId(NEW_ID)}
          >
            {t('warehouses.list.add')}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Card className="h-auto overflow-hidden">
          <Table
            data={visible}
            columns={columns}
            getRowId={(warehouse) => warehouse.id || NEW_ID}
            selectedRowId={selectedId}
            onRowClick={(warehouse) => setSelectedId(warehouse.id || NEW_ID)}
          />
          {visible.length === 0 ? (
            <p className="px-4 py-6 text-sm text-neutral-600">
              {search
                ? t('warehouses.list.no_matches', { query: search })
                : t('warehouses.list.empty')}
            </p>
          ) : null}
        </Card>

        {selected ? (
          <WarehouseForm
            key={`${selectedId}-${JSON.stringify(selected)}`}
            warehouse={selected}
            editable={editable}
            usedBy={
              selected.id ? storesUsing(routing, scope.stores, selected.id) : 0
            }
            saving={savingWarehouse}
            deleting={deletingWarehouse}
            onSave={(warehouse) =>
              saveWarehouse(warehouse, { onSuccess: (id) => setSelectedId(id) })
            }
            onDelete={() => remove(selected)}
            onDiscardNew={() => setSelectedId(warehouses[0]?.id ?? '')}
          />
        ) : null}
      </div>
    </div>
  )
}
